/**
 * When a sign-in cannot be renewed silently (Enterprise), the console asks once instead of leaving
 * the page: "Your sign-in needs renewing", Continue. Continue renews in a Microsoft popup, so the page
 * and everything typed on it stay; if the browser blocks the popup the page goes to Microsoft and
 * back, and drafts come back from sessionStorage (useDraft).
 */

import { AlertDialog, Button } from '@heroui/react';
import { useState } from 'react';

import { useAuth } from '../auth/AuthProvider.tsx';

export function RenewalDialog() {
  const { renewal, stepUp, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!renewal && stepUp) {
    // Step-up: remote control needs a recent sign-in. Same dialog, its own words.
    return (
      <AlertDialog.Backdrop isOpen isDismissable={false}>
        <AlertDialog.Container>
          <AlertDialog.Dialog className="sm:max-w-md">
            <AlertDialog.Header>
              <AlertDialog.Icon status="warning" />
              <AlertDialog.Heading>Sign in again to take control</AlertDialog.Heading>
            </AlertDialog.Header>
            <AlertDialog.Body>
              <p className="text-sm text-muted">{stepUp.message}</p>
              {failed && <p className="mt-2 text-sm text-danger">That sign-in did not finish. Try again, or choose Not now.</p>}
            </AlertDialog.Body>
            <AlertDialog.Footer>
              <Button variant="tertiary" onPress={() => (setFailed(false), stepUp.cancel())}>
                Not now
              </Button>
              <Button
                variant="primary"
                autoFocus
                isPending={busy}
                onPress={() => {
                  setBusy(true);
                  setFailed(false);
                  void stepUp
                    .confirm()
                    .catch(() => setFailed(true))
                    .finally(() => setBusy(false));
                }}
              >
                Sign in again
              </Button>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
    );
  }
  if (!renewal) return null;
  return (
    <AlertDialog.Backdrop isOpen isDismissable={false}>
      <AlertDialog.Container>
        <AlertDialog.Dialog className="sm:max-w-md">
          <AlertDialog.Header>
            <AlertDialog.Icon status="warning" />
            <AlertDialog.Heading>Your sign-in needs renewing</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body>
            <p className="text-sm text-muted">Microsoft asks you to confirm it is still you. What you have typed stays where it is.</p>
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button variant="tertiary" onPress={() => signOut()}>
              Sign out
            </Button>
            <Button
              variant="primary"
              autoFocus
              isPending={busy}
              onPress={() => {
                setBusy(true);
                void renewal.renew().finally(() => setBusy(false));
              }}
            >
              Continue
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}
